import { useState, useEffect, useRef, useCallback } from "react";
import { Container, Form, FormGroup, Label, Input, Button } from "reactstrap";
import SelectionBar from "./SelectionBar";
import CancellationModal from "./CancellationModal";
import styles from "../../styles/public/book-page.module.css";
import api from "../../services/api";
import moment from "moment";

const BookPage = ({ bookingData, onEditStep, updateBookingData, handleCancel, organization }) => {
  // Parse duration from organization.online_hold_time (format: "HH:MM:SS")
  const timeoutDuration = organization?.online_hold_time 
    ? moment.duration(organization.online_hold_time).asMilliseconds()
    : 5 * 60 * 1000; // Default to 5 minutes if not set
  const timeoutInMinutes = timeoutDuration / (60 * 1000);

  const [discountCodes, setDiscountCodes] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTimeout, setIsTimeout] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    couponCode: "",
    isFirstTime: false,
    isMilitary: false,
    isSenior: false,
    isLeagueMember: false,
    discountCode: "",
    wantsEmails: true,
    notes: "",
  });
  const firstNameInputRef = useRef(null);

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    const newFormData = {
      ...formData,
      [name]: newValue,
    };
    setFormData(newFormData);
    updateBookingData({ contactInfo: newFormData });
  };

  // Handle autofill detection - check if fields have values that weren't captured by onChange
  const handleBlur = useCallback(() => {
    const firstNameEl = document.getElementById('firstName');
    const lastNameEl = document.getElementById('lastName');
    const emailEl = document.getElementById('email');
    const phoneEl = document.getElementById('phone');

    const autofillData = {
      firstName: firstNameEl?.value || '',
      lastName: lastNameEl?.value || '',
      email: emailEl?.value || '',
      phone: phoneEl?.value || '',
    };

    // Check if any autofilled values differ from state
    const hasAutofilledChanges = 
      (autofillData.firstName && autofillData.firstName !== formData.firstName) ||
      (autofillData.lastName && autofillData.lastName !== formData.lastName) ||
      (autofillData.email && autofillData.email !== formData.email) ||
      (autofillData.phone && autofillData.phone !== formData.phone);

    if (hasAutofilledChanges) {
      const newFormData = {
        ...formData,
        ...autofillData,
      };
      setFormData(newFormData);
      updateBookingData({ contactInfo: newFormData });
    }
  }, [formData, updateBookingData]);

  const handleDiscount = (e) => {
    const { value, checked } = e.target;

    const newValue = checked ? value : null;

    const newFormData = {
      ...formData,
      discountCode: newValue,
    };

    setFormData(newFormData);
    updateBookingData({ contactInfo: newFormData });
  };

  const handleEditStep = (step) => {
    // Only cancel hold when editing a previous step (going backwards)
    // Don't cancel when we're about to move forward to payment
    if (step < 3) {
      handleCancel();
    }
    onEditStep(step);
  };

  const handleTimeout = useCallback(() => {
    setIsTimeout(true);
    handleCancel(true);
  }, [handleCancel]);

  const formattedDate = bookingData.date
    ? bookingData.date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
    })
    : null;

  useEffect(() => {
    if (bookingData.contactInfo) {
      setFormData((prev) => ({
        ...prev,
        ...bookingData.contactInfo,
      }));
    }
  }, [bookingData.contactInfo]);

  useEffect(() => {
    if (firstNameInputRef.current) {
      firstNameInputRef.current.focus();
    }
  }, []);

  useEffect(() => {

    const timeoutId = setTimeout(handleTimeout, timeoutDuration);

    const handlePageHide = () => {
      handleCancel();
    };

    window.addEventListener('pagehide', handlePageHide);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [bookingData, timeoutDuration, handleCancel, handleTimeout]);

  useEffect(() => {
    api.get('/discount-codes/')
      .then(res => setDiscountCodes(res.data.map(item => ({ id: item.id, code: item.code, description: item.description }))))
      .catch(console.log);
  }, []);

  return (
    <Container>
      <SelectionBar
        service={bookingData.service}
        date={formattedDate}
        time={bookingData.time}
        onEditStep={handleEditStep}
      />

      {(isTimeout) ? (
        <div className="alert alert-danger text-center mt-4" role="alert">
          Your hold has been cancelled due to inactivity.  Please update your booking information and try again.
        </div>
      ) : (

        <>
          <div className="text-center mb-4">
            <h2 className={styles["book-title"]}>Fill out your contact information and book</h2>
            <small>(Your hold will expire in {timeoutInMinutes.toFixed(0)} minutes)</small>
          </div>

          <div className={styles['booking-form']}>
            <Form onSubmit={(e) => e.preventDefault()} className="booking-form">
              <FormGroup>
                <Label for="firstName" className="required">
                  First Name *
                </Label>
                <Input
                  type="text"
                  name="firstName"
                  id="firstName"
                  placeholder="Enter your first name"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  innerRef={firstNameInputRef}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label for="lastName" className="required">
                  Last Name *
                </Label>
                <Input
                  type="text"
                  name="lastName"
                  id="lastName"
                  placeholder="Enter your last name"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label for="email" className="required">
                  Email *
                </Label>
                <Input
                  type="email"
                  name="email"
                  id="email"
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label for="phone" className="required">
                  Phone *
                </Label>
                <Input
                  type="phone"
                  name="phone"
                  id="phone"
                  placeholder="Enter your phone number"
                  value={formData.phone}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                />
              </FormGroup>

              <Label>
                Tell us about yourself
              </Label>

              <FormGroup check className="mb-3">
                <Input
                  type="checkbox"
                  name="isFirstTime"
                  id="isFirstTime"
                  checked={formData.isFirstTime}
                  onChange={handleInputChange}
                />
                <Label check for="isFirstTime">
                  I am a first time customer
                </Label>
              </FormGroup>

              <FormGroup check className="mb-3">
                {discountCodes.map((discount, index) => (
                  <div key={discount.code || index}>
                    <Label check className='mb-3'>
                      <Input
                        type="checkbox"
                        name="discountCode"
                        id="discountCode"
                        value={discount.id}
                        checked={parseInt(formData.discountCode) === discount.id}
                        onChange={handleDiscount}
                      />
                      {discount.description}
                    </Label>
                    <br />
                  </div>
                ))}
              </FormGroup>
              <FormGroup>
                <Label for="notes">Add a note to your reservation</Label>
                <Input
                  type="textarea"
                  name="notes"
                  id="notes"
                  placeholder="Please enter a message"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="4"
                />
              </FormGroup>

              <FormGroup check className="mb-3 border-dark">
                <Input
                  type="checkbox"
                  name="wantsEmails"
                  id="wantsEmails"
                  checked={formData.wantsEmails}
                  onChange={handleInputChange}
                />
                <Label check for="wantsEmails">
                  Email me news and loyalty offers from Stingers
                </Label>
              </FormGroup>

              <FormGroup>
                <Button color="link" className={styles["cancellation-link"]} onClick={toggleModal}>
                  Cancellation policy
                </Button>
              </FormGroup>

              <CancellationModal isOpen={isModalOpen} onClose={toggleModal} />
            </Form>
          </div>
        </>
      )}
    </Container>
  );
};

export default BookPage;